<template>
  <div class="has-tab-bar">
    <app-bar title="" :show-back-button="true" />
    <spinner-loader v-if="loading" />
    <div class="container">
      <h1>Uitbetaling</h1>
      <template v-if="settlement.data && Object.keys(settlement.data).length">
        <div class="border round-lg p-4 text-sm">
          <div class="flex justify-between">
            <span class="text-gray-600">Uitbetaald op</span>
            <span class="text-gray-600">
              {{ payoutDate ? payoutDate : 'Nog niet uitbetaald' }}
            </span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Bedrag uitbetaald</span>
            <span class="text-gray-600">
              {{ $n(amountPaid, 'currency') }}
            </span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Periode start</span>
            <span class="text-gray-600">{{ periodStart }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Periode einde</span>
            <span class="text-gray-600">{{ periodEnd }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Aantal transacties</span>
            <span class="text-gray-600">
              {{ settlement.data.number_of_transactions }}
            </span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Aantal terugbetalingen</span>
            <span class="text-gray-600">
              {{ settlement.data.number_of_refunds }}
            </span>
          </div>
        </div>
      </template>
      <template v-if="formattedSettlement">
        <h2>Transacties</h2>
        <div class="flex text-sm font-semibold py-1">
          <div class="w-24">Datum</div>
          <div class="flex-1">Deelnemer / Beschrijving</div>
          <div class="w-14">Bedrag</div>
        </div>
        <div
          v-for="settlementData in formattedSettlement.data"
          :key="settlementData.reference"
          data-test="settlement-transaction-row"
          class="flex text-sm py-1"
        >
          <div class="w-24 flex-shrink-0">
            {{ settlementData.formattedDate }}
          </div>
          <div class="flex-1 min-w-0 pr-2">
            <div
              v-if="settlementData.participant_name"
              class="font-semibold break-words"
              data-test="settlement-transaction-participant"
            >
              {{ settlementData.participant_name }}
            </div>
            <div class="text-gray-600 break-words">
              {{ settlementData.description }}
            </div>
          </div>
          <div class="w-14 flex-shrink-0 text-right">
            {{ $n(settlementData.amount_payable / 100, 'currency') }}
          </div>
        </div>
        <pagination
          v-if="lastPage > 1"
          :page="page"
          :last-page="lastPage"
          :has-more="hasMore"
          @previous="goToPreviousPage"
          @next="goToNextPage"
          @goto="goToPage"
        />
      </template>
    </div>
    <tab-bar selected="" />
  </div>
</template>

<script>
import AppBar from '@/components/AppBar'
import SpinnerLoader from '@/components/SpinnerLoader'
import TabBar from '@/components/TabBar'
import Pagination from '@/components/Pagination'
import { mapState } from 'vuex'
import api from '@/utils/api'
import { DateTime } from 'luxon'

export default {
  name: 'SettlementDetails',
  components: { AppBar, SpinnerLoader, TabBar, Pagination },
  data() {
    return {
      settlement: {},
      loading: false,
      page: 1,
      perPage: 25,
      hasMore: false,
      lastPage: 1
    }
  },
  computed: {
    ...mapState({}),
    formattedSettlement() {
      return this.settlement.transactions?.data?.length
        ? {
            ...this.settlement.transactions,
            data: this.settlement.transactions.data.map((row) => ({
              ...row,
              formattedDate: DateTime.fromMillis(row.date * 1000).toFormat(
                'dd-MM-yyyy'
              )
            }))
          }
        : null
    },
    payoutDate() {
      return this.settlement.data?.paid
        ? DateTime.fromMillis(this.settlement.data.paid * 1000).toFormat(
            'dd-MM-yyyy HH:mm'
          )
        : ''
    },
    amountPaid() {
      return this.settlement.data?.total_amount
        ? this.settlement.data.total_amount / 100
        : 0
    },
    periodStart() {
      return this.settlement.data?.period_start
        ? DateTime.fromMillis(
            this.settlement.data.period_start * 1000
          ).toFormat('dd-MM-yyyy HH:mm')
        : ''
    },
    periodEnd() {
      return this.settlement.data?.period_end
        ? DateTime.fromMillis(this.settlement.data.period_end * 1000).toFormat(
            'dd-MM-yyyy HH:mm'
          )
        : ''
    }
  },
  watch: {},
  mounted: async function () {
    await this.fetchSettlement()
  },
  methods: {
    async fetchSettlement() {
      this.loading = true
      const getSettlement = await api.get(
        `/payment/settlements/${this.$route.params.settlementUid}/${this.$route.params.specificationUid}`,
        {
          params: {
            page: this.page,
            perPage: this.perPage
          }
        }
      )
      if (getSettlement.status === 200) {
        this.settlement = getSettlement.data
        this.hasMore = !!this.settlement.transactions?.has_more
        this.lastPage = this.settlement.transactions?.last_page || 1
      }
      this.loading = false
    },
    async goToPreviousPage() {
      if (this.page > 1) {
        this.page--
        await this.fetchSettlement()
      }
    },
    async goToNextPage() {
      if (this.hasMore) {
        this.page++
        await this.fetchSettlement()
      }
    },
    async goToPage(pageNum) {
      if (pageNum !== this.page && pageNum >= 1 && pageNum <= this.lastPage) {
        this.page = pageNum
        await this.fetchSettlement()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/variables.scss';
</style>
